import React, { useState, useRef, useEffect } from "react";
import {
  StyleSheet,
  View,
  Pressable,
  Modal,
  Dimensions,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "./ThemedText";
import { useThemeColor } from "../hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import { trackButtonClick } from "@/services/analytics";

/**
 * Interface representing an individual action item inside the kebab menu.
 */
export interface IKebabActionItem {
  /** Unique identifier for the action */
  id: string;
  /** Display label for the action */
  label: string;
  /** Optional icon name from Ionicons or emoji string */
  icon?: keyof typeof Ionicons.glyphMap | string;
  /** Callback triggered when the action is pressed */
  onPress: () => void;
  /** Whether this action represents a destructive operation (rendered in danger color) */
  destructive?: boolean;
  /** Whether this action is currently disabled */
  disabled?: boolean;
  /** Optional testID */
  testID?: string;
}

/**
 * Properties for the ThemedKebabMenu component.
 */
export interface IThemedKebabMenuProps {
  /** List of actions available in the popout menu */
  actions: IKebabActionItem[];
  /** Whether to render in high-density compact mode */
  compact?: boolean;
  /** Optional style override for the kebab trigger button */
  triggerStyle?: StyleProp<ViewStyle>;
  /** Optional accessibility label for the trigger button */
  accessibilityLabel?: string;
  /** Optional testID for testing */
  testID?: string;
}

/**
 * Check if a string is likely an emoji rather than an Ionicons icon name.
 *
 * @param {string} icon - The icon string to check.
 * @returns {boolean} True if the string is an emoji.
 */
function isEmoji(icon: string): boolean {
  // If it's a known Ionicons kebab-friendly name with hyphens or standard words, it's not emoji
  return /\p{Extended_Pictographic}/u.test(icon);
}

/**
 * Standardized Archival Heirloom Kebab Action Menu.
 * Consolidates item card actions (Share, Edit, Delete, Export) into a single 3-dot trigger button.
 * Maximizes horizontal layout space for card titles and descriptions.
 *
 * @param {IThemedKebabMenuProps} props - Component properties.
 * @returns {React.JSX.Element | null} The rendered kebab menu or null if no actions.
 */
export function ThemedKebabMenu({
  actions,
  compact = false,
  triggerStyle,
  accessibilityLabel = "More options",
  testID,
}: IThemedKebabMenuProps) {
  const triggerRef = useRef<View>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuLayout, setMenuLayout] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const [hoveredActionId, setHoveredActionId] = useState<string | null>(null);
  const { popupMenu } = useComponentTheme();
  const cardBgColor = popupMenu.card.backgroundColor;
  const borderColor = popupMenu.card.borderColor;
  const tintColor = popupMenu.item.accentBarColor;
  const badgeBg = popupMenu.item.hoverBg;
  const dangerColor = popupMenu.item.destructiveTextColor;
  const textColor = popupMenu.item.textColor;
  const textMuted = popupMenu.item.mutedTextColor;
  const buttonHoverBorder = popupMenu.kebabTrigger.hoverBorderColor;

  const visibleActions = actions.filter((action) => !action.disabled);

  // Close when window is scrolled or resized on web
  useEffect(() => {
    if (!isOpen) return;
    if (Platform.OS === "web" && typeof window !== "undefined") {
      const handleScrollOrResize = () => {
        setIsOpen(false);
      };
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
    return undefined;
  }, [isOpen]);

  if (visibleActions.length === 0) {
    return null;
  }

  const handleToggle = (e?: any): void => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    setHoveredActionId(null);
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    const menuWidth = compact ? 160 : 180;
    const estimatedHeight = visibleActions.length * (compact ? 36 : 42) + 12;

    if (triggerRef.current) {
      if (Platform.OS === "web") {
        const node = triggerRef.current as any;
        if (typeof node.getBoundingClientRect === "function") {
          const rect = node.getBoundingClientRect();
          const screenWidth = Dimensions.get("window").width;
          const screenHeight = Dimensions.get("window").height;
          const opensUpward =
            rect.bottom + estimatedHeight > screenHeight && rect.top > estimatedHeight;
          const calculatedTop = opensUpward ? Math.max(8, rect.top - estimatedHeight - 4) : rect.bottom + 4;
          const calculatedLeft = Math.max(8, Math.min(rect.right - menuWidth, screenWidth - menuWidth - 8));

          setMenuLayout({
            top: calculatedTop,
            left: calculatedLeft,
          });
          setIsOpen(true);
          return;
        }
      }

      triggerRef.current.measureInWindow((x, y, width, height) => {
        const screenWidth = Dimensions.get("window").width;
        const screenHeight = Dimensions.get("window").height;
        const opensUpward =
          y + height + estimatedHeight > screenHeight && y > estimatedHeight;
        const calculatedTop = opensUpward ? Math.max(8, y - estimatedHeight - 4) : y + height + 4;
        const calculatedLeft = Math.max(8, Math.min(x + width - menuWidth, screenWidth - menuWidth - 8));

        setMenuLayout({
          top: calculatedTop,
          left: calculatedLeft,
        });
        setIsOpen(true);
      });
    } else {
      setIsOpen(true);
    }
  };

  const handleActionPress = (action: IKebabActionItem, e: any): void => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    trackButtonClick(`menu_${action.id || action.label}`, undefined, { label: action.label });
    setIsOpen(false);
    setHoveredActionId(null);
    action.onPress();
  };

  const triggerSize = compact ? popupMenu.kebabTrigger.sizeCompact : popupMenu.kebabTrigger.sizeStandard;
  const iconSize = compact ? popupMenu.kebabTrigger.iconSizeCompact : popupMenu.kebabTrigger.iconSizeStandard;

  return (
    <View style={styles.anchorContainer} collapsable={false}>
      <Pressable
        ref={triggerRef}
        onPress={handleToggle}
        onHoverIn={() => setIsTriggerHovered(true)}
        onHoverOut={() => setIsTriggerHovered(false)}
        {...Platform.select({
          web: {
            onMouseEnter: () => setIsTriggerHovered(true),
            onMouseLeave: () => setIsTriggerHovered(false),
            onPointerEnter: () => setIsTriggerHovered(true),
            onPointerLeave: () => setIsTriggerHovered(false),
          } as any,
          default: {},
        })}
        style={[
          styles.triggerButton,
          {
            width: triggerSize,
            height: triggerSize,
            borderRadius: popupMenu.kebabTrigger.borderRadius,
            backgroundColor: isOpen || isTriggerHovered ? popupMenu.kebabTrigger.activeBg : popupMenu.kebabTrigger.restingBg,
            borderColor: isOpen
              ? popupMenu.kebabTrigger.activeBorderColor
              : isTriggerHovered
              ? popupMenu.kebabTrigger.hoverBorderColor
              : popupMenu.kebabTrigger.restingBorderColor,
          },
          triggerStyle,
        ]}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ expanded: isOpen }}
        testID={testID}
      >
        <Ionicons
          name="ellipsis-vertical"
          size={iconSize}
          color={isOpen || isTriggerHovered ? popupMenu.kebabTrigger.activeIconColor : popupMenu.kebabTrigger.restingIconColor}
        />
      </Pressable>

      {isOpen && (
        <Modal
          transparent
          visible={isOpen}
          animationType="none"
          onRequestClose={() => {
            setIsOpen(false);
            setHoveredActionId(null);
          }}
        >
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={(e) => {
              e.stopPropagation();
              setIsOpen(false);
              setHoveredActionId(null);
            }}
            accessibilityLabel="Close options menu"
          />

          <View
            style={[
              styles.menuCard,
              {
                top: menuLayout.top,
                left: menuLayout.left,
                width: compact ? 160 : 180,
                backgroundColor: cardBgColor,
                borderColor: borderColor,
                borderRadius: popupMenu.card.borderRadius,
              },
            ]}
          >
            {visibleActions.map((action, index) => {
              const isDestructive = action.destructive === true;
              const isHovered = hoveredActionId === action.id;

              const itemBg = isHovered
                ? isDestructive
                  ? popupMenu.item.destructiveHoverBg
                  : badgeBg
                : "transparent";

              const leftBorderColor = isHovered
                ? isDestructive
                  ? dangerColor
                  : tintColor
                : "transparent";

              const itemColor = isDestructive ? dangerColor : textColor;

              return (
                <Pressable
                  key={action.id}
                  style={({ pressed }) => [
                    styles.menuItem,
                    compact && styles.menuItemCompact,
                    {
                      backgroundColor: pressed ? badgeBg : itemBg,
                      borderLeftWidth: 3,
                      borderLeftColor: leftBorderColor,
                    },
                    index < visibleActions.length - 1 && {
                      borderBottomWidth: StyleSheet.hairlineWidth,
                      borderBottomColor: borderColor,
                    },
                  ]}
                  onPress={(e) => handleActionPress(action, e)}
                  onHoverIn={() => setHoveredActionId(action.id)}
                  onHoverOut={() => setHoveredActionId((prev) => (prev === action.id ? null : prev))}
                  {...Platform.select({
                    web: {
                      onMouseEnter: () => setHoveredActionId(action.id),
                      onMouseLeave: () => setHoveredActionId((prev) => (prev === action.id ? null : prev)),
                      onPointerEnter: () => setHoveredActionId(action.id),
                      onPointerLeave: () => setHoveredActionId((prev) => (prev === action.id ? null : prev)),
                    } as any,
                    default: {},
                  })}
                  testID={action.testID}
                  accessibilityRole="button"
                >
                  {action.icon ? (
                    <View style={styles.iconContainer}>
                      {isEmoji(String(action.icon)) ? (
                        <ThemedText style={{ fontSize: compact ? 13 : 15 }}>
                          {action.icon}
                        </ThemedText>
                      ) : (
                        <Ionicons
                          name={action.icon as keyof typeof Ionicons.glyphMap}
                          size={compact ? 14 : 16}
                          color={itemColor}
                        />
                      )}
                    </View>
                  ) : null}
                  <ThemedText
                    style={[
                      styles.actionLabel,
                      compact && styles.actionLabelCompact,
                      { color: itemColor },
                      isDestructive && styles.destructiveLabel,
                      isHovered && styles.hoveredLabel,
                    ]}
                    numberOfLines={1}
                  >
                    {action.label}
                  </ThemedText>
                </Pressable>
              );
            })}
          </View>
        </Modal>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  anchorContainer: {
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
  },
  triggerButton: {
    borderRadius: 0,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
    ...Platform.select({
      web: {
        cursor: "pointer",
        transition: "background-color 0.12s ease, border-color 0.12s ease",
      } as any,
      default: {},
    }),
  },
  menuCard: {
    position: "absolute",
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 8,
    zIndex: 9999,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 8,
    ...Platform.select({
      web: {
        cursor: "pointer",
        transition: "background-color 0.12s ease, border-color 0.12s ease",
      } as any,
      default: {},
    }),
  },
  menuItemCompact: {
    paddingVertical: 7,
    paddingHorizontal: 10,
    gap: 6,
  },
  iconContainer: {
    width: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  actionLabel: {
    fontSize: 13,
    fontWeight: "500",
    flex: 1,
  },
  actionLabelCompact: {
    fontSize: 12,
  },
  destructiveLabel: {
    fontWeight: "600",
  },
  hoveredLabel: {
    fontWeight: "600",
  },
});
